import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Geometry, MultiPolygon } from 'ol/geom';
import { Fill, Stroke, Style} from 'ol/style';
import { VesselEnpBookAndChapterModel } from '../../src/models/vessel-enp-book-and-chapter-model';
import { Feature } from 'ol';

const _PROJECTION = (<any>window)["appenv"].projection;

export interface EnpLayerColors
{
    chapterBorderColor: string;
    bookBorderColor: string;
    fillColor: string;
}

export class EnpBooksAndChaptersLayer extends EventTarget
{
    private _enpLayer : VectorLayer<VectorSource<Geometry>> | null = null;

    constructor(colors?: Partial<EnpLayerColors>)
    {
        super();
        this.setColors(colors || {});
    }

    get colors(): EnpLayerColors
    {
        return this._colors;
    }

    get layers(): VectorLayer<VectorSource<Geometry>>[]
    {
        return this._layers;
    }

    async initialize(): Promise<void>
    {
        // Enp chapters
        this._vectorSource = new VectorSource();
        this._enpLayer = new VectorLayer({source: this._vectorSource, visible: false});
        this._layers.push(this._enpLayer);
        this._plotEnpChapters();
    }

    setColors(colors: Partial<EnpLayerColors>): void
    {
        this.colors.chapterBorderColor = colors.chapterBorderColor || this.colors.chapterBorderColor;
        this.colors.bookBorderColor = colors.bookBorderColor || this.colors.bookBorderColor;
        this.colors.fillColor = colors.fillColor || this.colors.fillColor;

        if (this._vectorSource){
            this._clear();
            this._plotEnpChapters();
        }
    }

    private _clear()
    {
        try {
            this._vectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    enable() {
        this._clear();
        this._plotEnpChapters().then(() => this._enpLayer!.setVisible(true));
    }

    disable() {
        this._enpLayer!.setVisible(false);
        this._clear();
    }

    private _colors: EnpLayerColors = {
        bookBorderColor: "purple",
        chapterBorderColor: "black",
        fillColor: "black"
    };

    private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
    private _vectorSource: VectorSource<Geometry>|null = null;

    private async _plotEnpChapters()
    {
        await VesselEnpBookAndChapterModel!.loadingPromise;
        
        const features: Feature<Geometry>[] = [];
        const chapters = VesselEnpBookAndChapterModel!.chapters;
        const booksFeatures = VesselEnpBookAndChapterModel!.booksFeatures;
        const books = VesselEnpBookAndChapterModel!.books!;
        const chapterBorderColor = this.colors.chapterBorderColor || "black";
        const booksBorderColor = this.colors.bookBorderColor || "purple";
        const fillColor = this.colors.fillColor || "black";

        const chaptersLine = new Style({
            stroke: new Stroke({
                color: chapterBorderColor, width: 1
            }),
            fill: new Fill({color: fillColor}),
        });

        const booksLine = new Style({
            stroke: new Stroke({
                color: booksBorderColor, width: 2
            }),
            fill: new Fill({color: `rgba(0, 0, 0, 0)`}), // invisible fill to add the label when hoovering over
        });

        chapters!.forEach(c => {
            const feature = new Feature({geometry: new MultiPolygon(<number[][][][]>c.geometry!.coordinates)});
            feature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
            feature.setStyle(chaptersLine);
            const name = `${c.properties.bookCatalogueNumber} - ${books.get(c.properties.bookCatalogueNumber!)!.properties.title} - Chapter ${c.properties.chapterNumber}`;
            const url = `com.maersk.publications://aenp.ukho.gov.uk/${c.properties.bookCatalogueNumber}#P${c.properties.firstPageNumber}`;
            feature.set('url', url);
            feature.set('text', name);
            feature.set('layer', "SailingDirectionsLayer");
            feature.set('featureType', c.properties.featureType);
            features.push(feature);
        });
        
        booksFeatures!.forEach(b => {
            const feature = new Feature({geometry: new MultiPolygon(<number[][][][]>b.geometry!.coordinates)});
            const book = books.get(b.properties.catalogueNumber!);
            feature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
            feature.setStyle(booksLine);
            const name = `${b.properties.catalogueNumber} - ${book!.properties.title} - ${book!.properties.editionNumber} edition (${book!.properties.editionName})`;
            const url = `com.maersk.publications://aenp.ukho.gov.uk/${b.properties.catalogueNumber}`;
            feature.set('url', url);
            feature.set('text', name);
            feature.set('layer', "SailingDirectionsLayer");
            feature.set('featureType', b.properties.featureType);
            features.push(feature);
        })      

        this._vectorSource!.addFeatures(features);
    }
};
